import React from 'react';
import { ShoppingCart, FileText, Clock, User, Phone, Truck, Store, Home, Wallet, CreditCard, CheckCircle, Package, Printer, Bot, Calendar, XCircle, Trash2, MessageCircle, History, Ear } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';

const OrdersDetailsModal = ({ formatAddress, getNextStatus, getNextStatusLabel, getStatusColor, getStatusLabel, loadingHistory, openDeleteModal, openRejectModal, orderHistory, printOrder, selectedOrder, setShowOrderDetails, showOrderDetails, updateOrderStatus }) => {
  return (
      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent className="glass-modal max-w-4xl max-h-[90vh] overflow-y-auto p-0 sm:p-6 w-[95vw] sm:w-full">
          {/* Mobile-optimized header */}
          <DialogHeader className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm p-3 sm:p-4 pr-12 sm:pr-14 border-b border-gray-700 sm:border-0 sm:bg-transparent">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="text-white text-lg sm:text-2xl font-bold flex items-center gap-2">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                  <ShoppingCart className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <span className="text-base sm:text-2xl">#{selectedOrder?.order_number}</span>
              </DialogTitle>
              <span className={`px-2 py-1 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold shadow-lg ${getStatusColor(selectedOrder?.status)}`}>
                {getStatusLabel(selectedOrder?.status, selectedOrder?.delivery_type)}
              </span>
            </div>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="p-3 sm:p-0 space-y-3 sm:space-y-4">
              {/* Data e Cliente - Layout compacto em mobile */}
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-4">
                {/* Data */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-2 sm:p-4 sm:bg-gradient-to-r sm:from-blue-500/10 sm:to-cyan-500/10">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide font-semibold hidden sm:block">Data do Pedido</p>
                      <p className="text-white font-semibold text-xs sm:text-lg truncate">
                        {new Date(selectedOrder.created_at).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cliente - Compacto */}
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-2 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide font-semibold hidden sm:block">Cliente</p>
                      <div className="flex items-center gap-1">
                        <p className="text-white font-bold text-xs sm:text-lg truncate">{selectedOrder.customer_name || 'Cliente'}</p>
                        {selectedOrder.source === 'whatsapp' && (
                          <span className="inline-flex items-center px-1 py-0.5 bg-green-500/20 rounded text-green-400 text-[9px] sm:text-xs font-medium">
                            <Bot className="w-2 h-2 sm:w-3 sm:h-3" />
                          </span>
                        )}
                        {selectedOrder.source === 'whatsapp_passive' && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-500/20 border border-purple-500/40 rounded text-purple-300 text-[9px] sm:text-xs font-medium"
                            title="Pedido criado via Escuta Passiva (IA Externa)"
                            data-testid={`order-detail-passive-${selectedOrder.id}`}
                          >
                            <Ear className="w-2 h-2 sm:w-3 sm:h-3" />
                            <span className="hidden sm:inline">Escuta</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scheduled Order Info - Compacto */}
              {selectedOrder.is_scheduled && selectedOrder.scheduled_date && selectedOrder.scheduled_time && (
                <div className="bg-indigo-500/20 border border-indigo-500/40 rounded-lg p-2 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-400" />
                    <div>
                      <span className="text-indigo-300 text-[10px] sm:text-xs font-bold uppercase">📅 Agendado</span>
                      <p className="text-white font-bold text-sm sm:text-xl">
                        {selectedOrder.scheduled_date} às {selectedOrder.scheduled_time}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Telefone - Clicável para WhatsApp */}
              {selectedOrder.customer_phone && (
                <a 
                  href={`https://wa.me/${selectedOrder.customer_phone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-lg p-2 sm:p-3 hover:bg-green-500/30 transition-colors"
                >
                  <Phone className="w-4 h-4 text-green-400" />
                  <span className="text-green-300 font-medium text-sm">{selectedOrder.customer_phone}</span>
                  <span className="ml-auto text-green-400 text-xs">Abrir WhatsApp →</span>
                </a>
              )}

              {/* Entrega e Pagamento - Grid compacto */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                {/* Entrega/Retirada */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-2 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    {selectedOrder.delivery_type === 'delivery' ? (
                      <Truck className="w-4 h-4 text-green-400" />
                    ) : selectedOrder.delivery_type === 'mesa' ? (
                      <Store className="w-4 h-4 text-blue-400" />
                    ) : (
                      <Home className="w-4 h-4 text-orange-400" />
                    )}
                    <span className="text-[10px] sm:text-xs text-gray-400 uppercase font-semibold">
                      {selectedOrder.delivery_type === 'delivery' ? 'Entrega' : 
                       selectedOrder.delivery_type === 'mesa' ? 'Mesa' : 'Retirada'}
                    </span>
                  </div>
                  {selectedOrder.delivery_type === 'mesa' && selectedOrder.table_number && (
                    <p className="text-white font-bold text-sm sm:text-lg">Mesa {selectedOrder.table_number}</p>
                  )}
                  {selectedOrder.delivery_type === 'delivery' && formatAddress(selectedOrder.address) && (
                    <p className="text-gray-300 text-[11px] sm:text-sm leading-tight line-clamp-2">
                      {formatAddress(selectedOrder.address)}
                    </p>
                  )}
                </div>

                {/* Pagamento */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-2 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    {selectedOrder.payment_method === 'dinheiro' ? (
                      <Wallet className="w-4 h-4 text-yellow-400" />
                    ) : selectedOrder.payment_method === 'pix' ? (
                      <span className="text-yellow-400 font-bold text-sm">₿</span>
                    ) : (
                      <CreditCard className="w-4 h-4 text-yellow-400" />
                    )}
                    <span className="text-[10px] sm:text-xs text-gray-400 uppercase font-semibold">Pagamento</span>
                  </div>
                  <p className="text-white font-bold text-sm sm:text-lg capitalize">
                    {selectedOrder.payment_method === 'dinheiro' && '💵 Dinheiro'}
                    {selectedOrder.payment_method === 'pix' && '📱 PIX'}
                    {selectedOrder.payment_method === 'cartao' && '💳 Cartão'}
                    {!['dinheiro', 'pix', 'cartao'].includes(selectedOrder.payment_method) && 
                      (selectedOrder.payment_method || '-')}
                  </p>
                  {selectedOrder.payment_method === 'pix' && selectedOrder.payment_status === 'paid' && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-green-500 rounded text-white text-[10px] font-bold">
                      <CheckCircle className="w-3 h-3" /> PAGO
                    </span>
                  )}
                  {selectedOrder.payment_method === 'dinheiro' && selectedOrder.change_for && (
                    <p className="text-yellow-300 text-[10px] sm:text-xs mt-1">
                      Troco: R$ {Number(selectedOrder.change_for).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              {/* Items - Compacto */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-2 sm:p-4 overflow-hidden">
                <div className="flex items-center gap-2 mb-2 sm:mb-4">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" />
                  <h3 className="text-sm sm:text-lg font-bold text-white">Itens</h3>
                  <span className="ml-auto text-gray-400 text-xs">{selectedOrder.items?.length || 0} itens</span>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="bg-slate-900/50 border border-slate-700 rounded-lg p-2 sm:p-4 overflow-hidden">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-start gap-1 sm:gap-2">
                            <span className="bg-orange-500 text-white text-[10px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded flex-shrink-0">
                              {item.quantity}x
                            </span>
                            <p className="text-white font-semibold text-xs sm:text-base break-words line-clamp-2">
                              {item.product_name || item.name || item.additional_name || 'Item'}
                            </p>
                          </div>
                          {item.additionals && item.additionals.length > 0 && (
                            <div className="mt-1 pl-2 border-l-2 border-green-500/30 space-y-1">
                              {/* Agrupar adicionais por grupo */}
                              {(() => {
                                // Agrupar adicionais por group_name
                                const grouped = {};
                                const ungrouped = [];
                                
                                item.additionals.forEach(add => {
                                  const groupName = add.group_name || add.additional_group_name;
                                  if (groupName) {
                                    if (!grouped[groupName]) grouped[groupName] = [];
                                    grouped[groupName].push(add);
                                  } else {
                                    ungrouped.push(add);
                                  }
                                });
                                
                                return (
                                  <>
                                    {/* Adicionais agrupados */}
                                    {Object.entries(grouped).map(([groupName, addItems]) => (
                                      <div key={groupName}>
                                        <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase font-semibold">{groupName}:</p>
                                        {addItems.map((add, idx) => {
                                          // Usar preço original se disponível (para adicionais incluídos no preço base)
                                          const displayPrice = add.original_price || add.price || 0;
                                          const isIncludedInBase = add.included_in_base_price || add.price === 0;
                                          
                                          return (
                                            <p key={idx} className="text-[10px] sm:text-xs text-green-400 pl-2">
                                              + {add.name || add.additional_name}
                                              {displayPrice > 0 && (
                                                <span className={`ml-1 ${isIncludedInBase ? 'text-blue-400' : 'text-yellow-400'}`}>
                                                  (R$ {Number(displayPrice).toFixed(2)})
                                                  {isIncludedInBase && <span className="text-[8px] text-gray-500 ml-1">✓ no preço</span>}
                                                </span>
                                              )}
                                            </p>
                                          );
                                        })}
                                      </div>
                                    ))}
                                    {/* Adicionais sem grupo */}
                                    {ungrouped.length > 0 && (
                                      <div>
                                        {ungrouped.map((add, idx) => {
                                          const displayPrice = add.original_price || add.price || 0;
                                          const isIncludedInBase = add.included_in_base_price || add.price === 0;
                                          
                                          return (
                                            <p key={idx} className="text-[10px] sm:text-xs text-green-400">
                                              + {add.name || add.additional_name}
                                              {displayPrice > 0 && (
                                                <span className={`ml-1 ${isIncludedInBase ? 'text-blue-400' : 'text-yellow-400'}`}>
                                                  (R$ {Number(displayPrice).toFixed(2)})
                                                  {isIncludedInBase && <span className="text-[8px] text-gray-500 ml-1">✓ no preço</span>}
                                                </span>
                                              )}
                                            </p>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          {item.notes && (
                            <p className="text-[10px] sm:text-xs text-yellow-400 mt-1 italic break-words">📝 {item.notes}</p>
                          )}
                        </div>
                        <p className="text-green-400 font-bold text-xs sm:text-base whitespace-nowrap flex-shrink-0">
                          R$ {(Number(item.total || item.price || 0) * (item.quantity || 1)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total - Destacado */}
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/40 rounded-lg p-3 sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-green-300 text-xs sm:text-sm font-semibold">Total do Pedido</p>
                    {selectedOrder.delivery_fee > 0 && (
                      <p className="text-gray-400 text-[10px] sm:text-xs">
                        (inclui taxa de R$ {Number(selectedOrder.delivery_fee).toFixed(2)})
                      </p>
                    )}
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-green-400">
                    R$ {Number(selectedOrder.total || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Observações */}
              {selectedOrder.notes && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 sm:p-4">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] sm:text-xs text-yellow-400 font-semibold uppercase">Observações</p>
                      <p className="text-white text-xs sm:text-sm mt-1">{selectedOrder.notes}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Motivo da Rejeição (se rejeitado) */}
              {selectedOrder.status === 'rejeitado' && selectedOrder.rejection_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 sm:p-4">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] sm:text-xs text-red-400 font-semibold uppercase">Motivo da Rejeição</p>
                      <p className="text-white text-xs sm:text-sm mt-1">{selectedOrder.rejection_reason}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Botões de Ação - Fixo no mobile */}
              <div className="sticky bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-sm p-3 -mx-3 sm:mx-0 sm:p-0 sm:bg-transparent sm:relative border-t border-gray-700 sm:border-0 mt-2 sm:mt-4">
                {/* Linha principal de ações */}
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={() => printOrder(selectedOrder)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all"
                    data-testid="modal-print-order-btn"
                  >
                    <Printer className="w-4 h-4" />
                    <span className="hidden sm:inline">Imprimir</span>
                  </button>
                  
                  {/* Botão Preparar/Próximo status - apenas para pedidos ativos */}
                  {(selectedOrder.status === 'pendente' || selectedOrder.status === 'em_producao') && (
                    <button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, getNextStatus(selectedOrder.status));
                        setShowOrderDetails(false);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg text-xs sm:text-sm font-bold shadow-lg transition-all"
                      data-testid="modal-advance-status-btn"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {selectedOrder.status === 'pendente' ? 'Preparar' : getNextStatusLabel(selectedOrder.status, selectedOrder.delivery_type)}
                    </button>
                  )}
                </div>

                {/* Linha secundária: Rejeitar e Excluir */}
                <div className="flex gap-2 sm:gap-3 mt-2">
                  {/* Botão Rejeitar - apenas para pedidos pendentes ou em produção */}
                  {!['pronto', 'cancelado', 'rejeitado'].includes(selectedOrder.status) && (
                    <button
                      onClick={() => openRejectModal(selectedOrder)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 hover:text-red-300 rounded-lg text-xs sm:text-sm font-semibold transition-all"
                      data-testid="modal-reject-order-btn"
                    >
                      <XCircle className="w-4 h-4" />
                      Rejeitar
                    </button>
                  )}

                  {/* Botão Excluir - disponível para todos os pedidos */}
                  <button
                    onClick={() => openDeleteModal(selectedOrder)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-gray-400 hover:text-gray-300 rounded-lg text-xs sm:text-sm font-semibold transition-all"
                    data-testid="modal-delete-order-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Excluir</span>
                  </button>
                </div>
              </div>

              {(orderHistory.length > 0 || loadingHistory) && (
                <div className="mt-2 bg-gray-800/40 border border-gray-700 rounded-xl p-3 sm:p-4" data-testid="order-history-section">
                  <div className="flex items-center gap-2 mb-2.5">
                    <History className="w-4 h-4 text-amber-400" />
                    <h4 className="text-sm font-semibold text-amber-200">Histórico de alterações</h4>
                    {orderHistory.length > 0 && (
                      <span className="text-[10px] uppercase tracking-wider text-amber-300/70 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
                        {orderHistory.length} {orderHistory.length === 1 ? 'alteração' : 'alterações'}
                      </span>
                    )}
                  </div>
                  {loadingHistory ? (
                    <p className="text-xs text-gray-400">Carregando…</p>
                  ) : (
                    <ol className="relative border-l-2 border-amber-500/30 ml-1 space-y-3">
                      {orderHistory.map((h) => {
                        const dt = h.edited_at ? new Date(h.edited_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
                        const added = h.diff?.added || [];
                        const removed = h.diff?.removed || [];
                        const changed = h.diff?.changed || [];
                        const fromT = h.totals?.from;
                        const toT = h.totals?.to;
                        return (
                          <li key={h.id} className="pl-4 relative">
                            <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-amber-500 border-2 border-gray-900 shadow" />
                            <p className="text-xs text-gray-400">
                              <span className="text-gray-200 font-medium">{h.edited_by_email || 'usuário'}</span>
                              <span> · {dt}</span>
                              {h.whatsapp_sent && (
                                <span className="ml-2 inline-flex items-center gap-1 text-green-400">
                                  <MessageCircle className="w-3 h-3" /> cliente notificado
                                </span>
                              )}
                            </p>
                            <div className="mt-1.5 space-y-0.5 text-xs">
                              {added.map((a, i) => (
                                <p key={`a${i}`} className="text-green-300">+ {a.qty}× {a.name}</p>
                              ))}
                              {removed.map((r, i) => (
                                <p key={`r${i}`} className="text-red-300">− {r.qty}× {r.name}</p>
                              ))}
                              {changed.map((c, i) => (
                                <p key={`c${i}`} className="text-blue-300">↺ {c.name} ({c.from}→{c.to})</p>
                              ))}
                              {fromT != null && toT != null && fromT !== toT && (
                                <p className="text-amber-200 font-medium mt-1">
                                  Total: R$ {fromT.toFixed(2)} → <span className="font-bold">R$ {toT.toFixed(2)}</span>
                                </p>
                              )}
                              {added.length === 0 && removed.length === 0 && changed.length === 0 && (
                                <p className="text-gray-500 italic">Sem alterações de itens (campos do pedido foram editados).</p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};

export default OrdersDetailsModal;
